import { useState } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Badge } from '../atoms/Badge/Badge';
import { Button } from '../atoms/Button/Button';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { deriveStatus, useLatestParameters } from '../../hooks/useLatestMeasurements';
import { useLatestProbeValues } from '../../hooks/useController';
import type { ProbeValue } from '../../api/controller';
import type { HistorySource, LatestParameter } from '../../services/measurementService';
import { formatMeasuredAt, measuredAtDateTimeAttr } from '../../utils/measurementDate';
import { formatDateTime } from '../controller/controllerFormat';
import {
  KNOWN_PARAMETER_IDS,
  formatNumber,
  parameterLabel,
  parameterStatusKey,
  parameterStatusTone,
  sourceLabelKey,
} from './measurementHistoryUtils';
import './LatestValuesTable.css';

export interface LatestValuesTableProps {
  aquariumId: string;
}

/** The badge shows ICP / Manuell / Foto — a Fauna Marin import is an ICP analysis. */
function badgeSource(source: HistorySource): HistorySource {
  return source === 'fauna_marin' ? 'icp' : source;
}

const KNOWN_ORDER = new Map<string, number>(KNOWN_PARAMETER_IDS.map((id, index) => [id, index]));

/** Hand-test parameters first (form order), then ICP elements by name. */
export function orderLatestParameters(parameters: readonly LatestParameter[]): LatestParameter[] {
  return [...parameters].sort((a, b) => {
    const ia = KNOWN_ORDER.get(a.parameterId) ?? Number.MAX_SAFE_INTEGER;
    const ib = KNOWN_ORDER.get(b.parameterId) ?? Number.MAX_SAFE_INTEGER;
    return ia - ib || a.parameterName.localeCompare(b.parameterName);
  });
}

/** One row of the table: the latest test and / or the latest tank probe value of a parameter. */
export interface LatestRow {
  parameterId: string;
  name: string;
  test: LatestParameter | null;
  probe: ProbeValue | null;
}

/**
 * Test rows in their usual order, each with the probe value of the same
 * parameter; parameters measured only by a probe (e.g. redox) follow as rows
 * of their own. Probe values sit beside the tests — never instead of them.
 */
export function mergeProbeRows(
  tests: readonly LatestParameter[],
  probes: readonly ProbeValue[],
  probeName: (parameterId: string) => string,
): LatestRow[] {
  const probeById = new Map(probes.map((p) => [p.parameterId, p]));
  const rows: LatestRow[] = orderLatestParameters(tests).map((test) => ({
    parameterId: test.parameterId,
    name: test.parameterName,
    test,
    probe: probeById.get(test.parameterId) ?? null,
  }));
  const tested = new Set(tests.map((t) => t.parameterId));
  const probeOnly = probes
    .filter((p) => !tested.has(p.parameterId))
    .map((probe) => ({ parameterId: probe.parameterId, name: probeName(probe.parameterId), test: null, probe }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return [...rows, ...probeOnly];
}

function ProbeCell({ probe, language }: { probe: ProbeValue; language: string }) {
  const { t } = useTranslation();
  const unit = probe.unit ?? '';
  const status = deriveStatus(probe.target, probe.value);
  const statusKey = parameterStatusKey(status);
  const window =
    probe.min !== null && probe.max !== null
      ? t('controller.probe.window', { min: formatNumber(probe.min, language), max: formatNumber(probe.max, language) })
      : null;
  return (
    <span className="latest-values-table__probe">
      <span className="latest-values-table__probe-value">
        <span className="latest-values-table__numeric-text">{`${formatNumber(probe.value, language)} ${unit}`.trim()}</span>
        {statusKey !== null && <Badge tone={parameterStatusTone(status)}>{t(statusKey)}</Badge>}
      </span>
      <Text as="span" size="xs" color="secondary">
        {[probe.origin ?? t('controller.probe.controller'), formatDateTime(probe.measuredAt, language), window]
          .filter(Boolean)
          .join(' · ')}
      </Text>
    </span>
  );
}

/**
 * The newest value per parameter of the aquarium (release 1.5.0, feature 2) —
 * the same data as the dashboard tiles (GET …/measurements/latest) — and,
 * in its own column, the latest aquarium-controller probe value of the
 * parameter (GET …/controller/probe-values, tank probes only) with its target
 * status. Probe values never replace a test value and never raise alerts. Renders
 * its loading and error states; with no values at all it renders nothing,
 * because the page shows its empty state ("Noch keine Messwerte erfasst").
 */
export function LatestValuesTable({ aquariumId }: LatestValuesTableProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'de';
  const query = useLatestParameters(aquariumId);
  // Probe values are an addition: while they load or if they fail, the test values show as before.
  const probes = useLatestProbeValues(aquariumId).data ?? [];
  const [showAll, setShowAll] = useState(false);

  if (query.isPending) {
    return (
      <LiveRegion busy>
        <Text>{t('measurement.latest.loading')}</Text>
      </LiveRegion>
    );
  }

  if (query.isError) {
    return (
      <LoadErrorState
        compact
        live="polite"
        title={t('measurement.latest.loadError')}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const rows = mergeProbeRows(query.data, probes, (id) => String(t(`controller.parameter.${id}`)));
  if (rows.length === 0) return null;
  const showProbe = probes.length > 0;
  // Hand-test parameters and probe values first; ICP trace elements on request — on a phone
  // ~55 stacked rows buried "what is my KH?" (usability audit §6).
  const isCore = (row: LatestRow) => KNOWN_ORDER.has(row.parameterId) || row.probe !== null;
  const coreRows = rows.filter(isCore);
  const moreRows = coreRows.length > 0 ? rows.filter((row) => !isCore(row)) : [];
  const visibleRows = showAll || coreRows.length === 0 ? rows : coreRows;

  return (
    <div className="latest-values-table__frame">
      <table className="latest-values-table">
        <caption className="latest-values-table__caption">{t('measurement.latest.caption')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('measurement.history.table.parameter')}</th>
            <th scope="col" className="latest-values-table__numeric">{t('measurement.history.table.value')}</th>
            <th scope="col">{t('measurement.history.table.date')}</th>
            <th scope="col">{t('measurement.history.table.source')}</th>
            {showProbe && <th scope="col">{t('controller.probe.column')}</th>}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row) => {
            const { test } = row;
            const latest = test?.latest;
            return (
              <tr key={row.parameterId}>
                <th scope="row">{parameterLabel(t, row.parameterId, row.name)}</th>
                <td className="latest-values-table__numeric" data-label={t('measurement.history.table.value')}>
                  {latest ? `${formatNumber(latest.value, language)} ${latest.unit}`.trim() : '–'}
                </td>
                <td data-label={t('measurement.history.table.date')}>
                  {latest ? <time dateTime={measuredAtDateTimeAttr(latest)}>{formatMeasuredAt(latest, language)}</time> : '–'}
                </td>
                <td data-label={t('measurement.history.table.source')}>
                  {latest ? <Badge>{t(sourceLabelKey(badgeSource(latest.source)))}</Badge> : '–'}
                </td>
                {showProbe && (
                  <td data-label={t('controller.probe.column')}>
                    {row.probe ? <ProbeCell probe={row.probe} language={language} /> : '–'}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {moreRows.length > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="latest-values-table__toggle"
          aria-expanded={showAll}
          onClick={() => setShowAll((v) => !v)}
          data-testid="latest-values-toggle"
        >
          {showAll ? t('measurement.latest.showFewer') : t('measurement.latest.showAll', { count: moreRows.length })}
        </Button>
      )}
    </div>
  );
}

export default LatestValuesTable;
